/**
 * Financial Aid Projection Entities
 *
 * Read models and integration shapes aligned to AppSync types.
 */
import { z } from "zod";
import {
  FinancialAidApplicationSchema,
  FinancialAidAssignedStaffUserId,
  FinancialAidDeadlineExtensionSchema,
  FinancialAidLinkActivityEventTypeSchema,
  FinancialAidSubmissionTimingSchema,
  ReviewFlagSchema,
  ScholarshipRecommendationSchema,
  StaffDecisionSchema,
  FinalAwardSchema,
} from "./financial-aid-application";
import {
  FinancialAidStatus,
  RequiredFinancialDocumentType,
  AppealStatus,
  InfoRequestStatus,
  AppealEventType,
  GradeLevel,
  FinancialAidFamilyMatchType,
} from "../types/enums";
import {
  FinancialAidApplicationId,
  FinancialAidCode,
  UserId,
  UnixTimestamp,
  AcademicYear,
  ApplicantId,
} from "../types/branded";
import {
  DocumentChecklistItemSchema,
  YstStudentEntrySchema,
} from "../value-objects/financial-aid";

export const TimelineEventSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  eventType: z.string(),
  isMajor: z.boolean(),
  occurredAt: UnixTimestamp,
  actorId: z.string().nullable().optional(),
  actorRole: z.string().nullable().optional(),
  summary: z.string(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type TimelineEvent = z.infer<typeof TimelineEventSchema>;

export const FinancialAidDocumentItemSchema = DocumentChecklistItemSchema.omit({
  notes: true,
}).extend({
  s3Key: z.string().nullable().optional(),
  fileName: z.string().nullable().optional(),
  fileType: z.string().nullable().optional(),
  fileSizeBytes: z.number().int().positive().nullable().optional(),
  confirmedAt: UnixTimestamp.nullable().optional(),
  downloadUrl: z.string().url().nullable().optional(),
});
export type FinancialAidDocumentItem = z.infer<
  typeof FinancialAidDocumentItemSchema
>;

export const FinancialAidDocumentStatusSchema = z.object({
  applicationId: FinancialAidApplicationId,
  items: z.array(FinancialAidDocumentItemSchema).default([]),
  allRequiredUploaded: z.boolean(),
  staffOverride: z.boolean(),
  missingDocuments: z.array(RequiredFinancialDocumentType).default([]),
});
export type FinancialAidDocumentStatus = z.infer<
  typeof FinancialAidDocumentStatusSchema
>;

export const FinancialAidDocumentChecklistResponseSchema = z.object({
  applicationId: FinancialAidApplicationId,
  checklist: z.array(FinancialAidDocumentItemSchema).default([]),
  updatedAt: UnixTimestamp,
});
export type FinancialAidDocumentChecklistResponse = z.infer<
  typeof FinancialAidDocumentChecklistResponseSchema
>;

export const FinancialAidReviewActivityEventTypeSchema = z.enum([
  ...FinancialAidLinkActivityEventTypeSchema.options,
  "application_submitted",
  "application_updated",
]);
export type FinancialAidReviewActivityEventType = z.infer<
  typeof FinancialAidReviewActivityEventTypeSchema
>;

export const FinancialAidReviewListItemSchema = z.object({
  id: FinancialAidApplicationId,
  updatedAt: UnixTimestamp,
  code: FinancialAidCode,
  academicYear: AcademicYear,
  familyName: z.string(),
  status: FinancialAidStatus,
  grade: GradeLevel.nullable().optional(),
  studentCount: z.number().int().nonnegative(),
  flagCount: z.number().int().nonnegative(),
  submittedAt: UnixTimestamp.nullable().optional(),
  assignedTo: FinancialAidAssignedStaffUserId.nullable().optional(),
  assigneeName: z.string().nullable().optional(),
  recommendedPercent: z.number().min(0).max(100).nullable().optional(),
  hasUnresolvedFlags: z.boolean(),
  priority: z.string().nullable().optional(),
  invitedEmail: z.string().email().nullable().optional(),
  lastActivity: z
    .object({
      eventType: FinancialAidReviewActivityEventTypeSchema,
      occurredAt: UnixTimestamp,
    })
    .nullable()
    .optional(),
  lastActivityAt: UnixTimestamp.nullable().optional(),
  staleInvite: z.boolean().optional(),
  deadlineExtensions: z.array(FinancialAidDeadlineExtensionSchema).default([]),
  submissionTiming: FinancialAidSubmissionTimingSchema.nullable().optional(),
  isLateSubmission: z.boolean().default(false),
});
export type FinancialAidReviewListItem = z.infer<
  typeof FinancialAidReviewListItemSchema
>;

export const FinancialAidReviewListResponseSchema = z.object({
  items: z.array(FinancialAidReviewListItemSchema).default([]),
  totalCount: z.number().int().nonnegative(),
  nextToken: z.string().nullable().optional(),
});
export type FinancialAidReviewListResponse = z.infer<
  typeof FinancialAidReviewListResponseSchema
>;

export const FinancialAidReviewDetailSchema = z.object({
  application: FinancialAidApplicationSchema,
  students: z.array(YstStudentEntrySchema).default([]),
  familyInfo: z.record(z.string(), z.unknown()).nullable().optional(),
  financials: z.record(z.string(), z.unknown()).nullable().optional(),
  documents: z.array(FinancialAidDocumentItemSchema).default([]),
  flags: z.array(ReviewFlagSchema).default([]),
  recommendation: ScholarshipRecommendationSchema.nullable().optional(),
  decisionHistory: z.array(StaffDecisionSchema).default([]),
  timeline: z.array(TimelineEventSchema).default([]),
});
export type FinancialAidReviewDetail = z.infer<
  typeof FinancialAidReviewDetailSchema
>;

export const FinancialAidStatusHistoryEntrySchema = z.object({
  status: FinancialAidStatus,
  changedAt: UnixTimestamp,
  changedBy: UserId.nullable().optional(),
  note: z.string().nullable().optional(),
  parentResponse: z.string().nullable().optional(),
});
export type FinancialAidStatusHistoryEntry = z.infer<
  typeof FinancialAidStatusHistoryEntrySchema
>;

export const FinancialAidStatusHistoryResponseSchema = z.object({
  entries: z.array(FinancialAidStatusHistoryEntrySchema).default([]),
});
export type FinancialAidStatusHistoryResponse = z.infer<
  typeof FinancialAidStatusHistoryResponseSchema
>;

export const FinancialAidStatusEventSchema = z.object({
  id: z.string(),
  applicationId: FinancialAidApplicationId,
  status: FinancialAidStatus,
  actorId: UserId.nullable().optional(),
  actorRole: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  occurredAt: UnixTimestamp,
});
export type FinancialAidStatusEvent = z.infer<
  typeof FinancialAidStatusEventSchema
>;

export const FinancialAidInfoRequestSchema = z.object({
  id: z.string(),
  applicationId: FinancialAidApplicationId,
  requestedInfo: z.string(),
  moreInfoRequestId: z.string().nullable().optional(),
  requestedAt: UnixTimestamp,
  responseDeadline: UnixTimestamp,
  respondedAt: UnixTimestamp.nullable().optional(),
  responseNotes: z.string().nullable().optional(),
  status: InfoRequestStatus,
});
export type FinancialAidInfoRequest = z.infer<
  typeof FinancialAidInfoRequestSchema
>;

export const FinancialAidAppealEventSchema = z.object({
  id: z.string(),
  appealId: z.string(),
  eventType: AppealEventType,
  occurredAt: UnixTimestamp,
  actorId: UserId.nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type FinancialAidAppealEvent = z.infer<
  typeof FinancialAidAppealEventSchema
>;

export const FinancialAidParentStatusViewSchema = z.object({
  applicationId: FinancialAidApplicationId,
  code: FinancialAidCode.nullable().optional(),
  status: FinancialAidStatus,
  statusLabel: z.string(),
  submittedAt: UnixTimestamp.nullable().optional(),
  lastUpdatedAt: UnixTimestamp.nullable().optional(),
  decision: StaffDecisionSchema.nullable().optional(),
  finalAward: FinalAwardSchema.nullable().optional(),
  nextSteps: z.array(z.string()).default([]),
  requestedInfo: z.string().nullable().optional(),
  moreInfoRequestId: z.string().nullable().optional(),
  responseDeadline: UnixTimestamp.nullable().optional(),
  canAppeal: z.boolean(),
  appealDeadline: UnixTimestamp.nullable().optional(),
  documentsComplete: z.boolean(),
  missingDocuments: z.array(z.string()).default([]),
});
export type FinancialAidParentStatusView = z.infer<
  typeof FinancialAidParentStatusViewSchema
>;

export const FinancialAidAppealSchema = z.object({
  id: z.string().uuid(),
  applicationId: FinancialAidApplicationId,
  status: AppealStatus,
  explanation: z.string().nullable().optional(),
  filedAt: UnixTimestamp,
  filedBy: z.string(),
  outcome: z.string().nullable().optional(),
  outcomeExplanation: z.string().nullable().optional(),
  decidedAt: UnixTimestamp.nullable().optional(),
  decidedBy: UserId.nullable().optional(),
  awardedPercent: z.number().min(0).max(100).nullable().optional(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
});
export type FinancialAidAppeal = z.infer<typeof FinancialAidAppealSchema>;

export const FinancialAidAppealSummarySchema = z.object({
  id: z.string().uuid(),
  applicationId: FinancialAidApplicationId,
  applicationCode: FinancialAidCode,
  familyName: z.string(),
  status: AppealStatus,
  daysPending: z.number().int().nonnegative(),
  filedAt: UnixTimestamp,
  severity: z.string(),
  briefReason: z.string(),
});
export type FinancialAidAppealSummary = z.infer<
  typeof FinancialAidAppealSummarySchema
>;

export const FinancialAidAppealHistoryResponseSchema = z.object({
  appeals: z.array(FinancialAidAppealSchema).default([]),
  totalAppeals: z.number().int().nonnegative(),
});
export type FinancialAidAppealHistoryResponse = z.infer<
  typeof FinancialAidAppealHistoryResponseSchema
>;

export const FinancialAidAppealListResponseSchema = z.object({
  appeals: z.array(FinancialAidAppealSummarySchema).default([]),
  totalCount: z.number().int().nonnegative(),
  nextToken: z.string().nullable().optional(),
});
export type FinancialAidAppealListResponse = z.infer<
  typeof FinancialAidAppealListResponseSchema
>;

export const FinancialAidReportSchema = z.object({
  reportType: z.string(),
  generatedAt: UnixTimestamp,
  academicYear: z.string(),
  data: z.record(z.string(), z.unknown()).nullable().optional(),
  summary: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type FinancialAidReport = z.infer<typeof FinancialAidReportSchema>;

export const FinancialAidReportExportSchema = z.object({
  downloadUrl: z.string(),
  contentType: z.string(),
  fileName: z.string(),
  expiresAt: UnixTimestamp,
});
export type FinancialAidReportExport = z.infer<
  typeof FinancialAidReportExportSchema
>;

export const FinancialAidApplicationReportAppendixSourceSchema = z.object({
  documentId: z.string().nullable().optional(),
  documentType: z.string(),
  fileName: z.string().nullable().optional(),
  contentType: z.string().nullable().optional(),
  status: z.enum(["included", "pending", "unsupported", "missing", "failed"]),
  message: z.string().nullable().optional(),
});
export type FinancialAidApplicationReportAppendixSource = z.infer<
  typeof FinancialAidApplicationReportAppendixSourceSchema
>;

export const FinancialAidApplicationReportAppendixSchema = z.object({
  includedCount: z.number().int().nonnegative(),
  omittedCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  sources: z
    .array(FinancialAidApplicationReportAppendixSourceSchema)
    .default([]),
});
export type FinancialAidApplicationReportAppendix = z.infer<
  typeof FinancialAidApplicationReportAppendixSchema
>;

export const FinancialAidApplicationReportArtifactSchema = z.object({
  artifactId: z.string(),
  applicationId: z.string(),
  variant: z.enum(["summary", "full", "yst"]),
  fileName: z.string(),
  contentType: z.string(),
  generatedAt: UnixTimestamp,
  generatedBy: z.string(),
  fileSizeBytes: z.number().int().nonnegative().optional(),
  appUrl: z.string(),
  appendix: FinancialAidApplicationReportAppendixSchema.optional(),
});
export type FinancialAidApplicationReportArtifact = z.infer<
  typeof FinancialAidApplicationReportArtifactSchema
>;

export const FinancialAidApplicationReportGenerationResultSchema =
  FinancialAidApplicationReportArtifactSchema.extend({
    downloadUrl: z.string(),
    expiresAt: UnixTimestamp,
  });
export type FinancialAidApplicationReportGenerationResult = z.infer<
  typeof FinancialAidApplicationReportGenerationResultSchema
>;

export const FinancialAidApplicationReportArtifactListSchema = z.object({
  applicationId: z.string(),
  artifacts: z.array(FinancialAidApplicationReportArtifactSchema).default([]),
});
export type FinancialAidApplicationReportArtifactList = z.infer<
  typeof FinancialAidApplicationReportArtifactListSchema
>;

export const FinancialAidApplicationReportDownloadUrlSchema = z.object({
  artifactId: z.string(),
  applicationId: z.string(),
  variant: z.enum(["summary", "full", "yst"]),
  fileName: z.string(),
  downloadUrl: z.string(),
  expiresAt: UnixTimestamp,
});
export type FinancialAidApplicationReportDownloadUrl = z.infer<
  typeof FinancialAidApplicationReportDownloadUrlSchema
>;

export const FinancialAidLetterResultSchema = z.object({
  letterId: z.string(),
  letterType: z.string(),
  downloadUrl: z.string(),
  emailSent: z.boolean(),
  emailSentAt: UnixTimestamp.nullable().optional(),
  expiresAt: UnixTimestamp,
});
export type FinancialAidLetterResult = z.infer<
  typeof FinancialAidLetterResultSchema
>;

export const FinancialAidDocumentUploadResponseSchema = z.object({
  uploadUrl: z.string(),
  s3Key: z.string(),
  documentId: z.string(),
  expiresAt: UnixTimestamp,
  uploadHeaders: z.record(z.string(), z.string()).optional(),
});
export type FinancialAidDocumentUploadResponse = z.infer<
  typeof FinancialAidDocumentUploadResponseSchema
>;

export const FinancialAidOpenCycleSchema = z.object({
  academicYear: AcademicYear.nullable(),
  schoolYear: z.string().nullable(),
  openDate: z.string().nullable().optional(),
  onTimeDeadline: z.string().nullable().optional(),
  hardCutoffDeadline: z.string().nullable().optional(),
  lateFeeAmount: z.number().nonnegative(),
  submissionRequirement: z
    .enum(["open_cycle", "registration_fee_paid"])
    .default("open_cycle"),
  isOpen: z.boolean(),
  isClosed: z.boolean(),
  isLateWindow: z.boolean(),
});
export type FinancialAidOpenCycle = z.infer<typeof FinancialAidOpenCycleSchema>;

export const FinancialAidFamilyLookupMatchSchema = z.object({
  familyId: z.string().uuid(),
  familyName: z.string(),
  applicantId: ApplicantId.nullable().optional(),
  parentContactId: z.string().nullable().optional(),
  parentFirstName: z.string().nullable().optional(),
  parentLastName: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  studentCount: z.number().int().nonnegative().optional(),
  primaryContact: z
    .object({
      id: z.string().nullable().optional(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      email: z.string().email().nullable().optional(),
    })
    .nullable()
    .optional(),
  existingApplication: z
    .object({
      applicationId: FinancialAidApplicationId,
      academicYear: AcademicYear,
      status: FinancialAidStatus,
      submittedAt: UnixTimestamp.nullable().optional(),
      lastActivityAt: UnixTimestamp.nullable().optional(),
      // INT-594: invite destination email so staff resend messaging can show
      // where the existing in-progress link goes (additive, optional).
      invitedEmail: z.string().email().nullable().optional(),
    })
    .nullable()
    .optional(),
  matchType: FinancialAidFamilyMatchType,
  // INT-594/INT-583: candidates can come from family records, invite-only
  // rows, or submitted/decision FA rows that have no Students/Families record.
  sourceKind: z
    .enum(["family_record", "invite_application", "financial_aid_application"])
    .optional(),
  inviteApplicationId: FinancialAidApplicationId.nullable().optional(),
});
export type FinancialAidFamilyLookupMatch = z.infer<
  typeof FinancialAidFamilyLookupMatchSchema
>;

export const FinancialAidFamilyLookupResponseSchema = z.object({
  matches: z.array(FinancialAidFamilyLookupMatchSchema).default([]),
  totalCount: z.number().int().nonnegative(),
});
export type FinancialAidFamilyLookupResponse = z.infer<
  typeof FinancialAidFamilyLookupResponseSchema
>;

export const FinancialAidInviteLinkResultSchema = z.object({
  applicationId: FinancialAidApplicationId.nullable().optional(),
  email: z.string().email().nullable().optional(),
  status: FinancialAidStatus.nullable().optional(),
  inviteToken: z.string().uuid().nullable().optional(),
  inviteUrl: z.string().url(),
  academicYear: AcademicYear.nullable().optional(),
  lastSentAt: UnixTimestamp.nullable().optional(),
  resendCount: z.number().int().nonnegative(),
});
export type FinancialAidInviteLinkResult = z.infer<
  typeof FinancialAidInviteLinkResultSchema
>;
